import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Row, Col } from 'react-flexbox-grid';
import {
  splitEvery,
  map,
  keys,
  pick,
  filter,
  isNil,
  complement,
  compose,
  prop,
  take,
  path as rPath,
  without,
} from 'ramda';

import { createSummaryTile } from '../../containers/SummaryLoaderContainer';
import { formatDate } from 'lib/dateDisplay';

import { path } from 'routes/Documents/routes/Status';

import icon from '../../assets/status.svg';
import Indicator, { EDSSIndicator } from './Indicator';
import {
  isLegacyDocument,
  historyDashFields,
  historyViewFields,
  historyViewLegacyFields,
} from '../../../Documents/routes/Status/Utils';

const section = s => (s ? `status.${s}.titleShort` : null);
const descr = (s, n) => (s && n >= 0 ? `status.${s}.${n}` : null);
const accessData = data => prop('sections', data);
const getPrEDSS = doc => rPath(['sections', 'prEDSS'], doc);
const isLegacyStatus = data => Object.keys(accessData(data)).some(k => k !== 'date' && k.indexOf('VASValue') < 0);

// Works like a whitelist, returns a new object with only the specified keys
const pickSections = (oldData = false, legacyData = false) =>
  pick(oldData ? historyViewLegacyFields : legacyData ? without(['prEDSS'], historyViewFields) : historyDashFields);

const filterKeys = (oldData, legacyData) =>
  compose(splitEvery(2), keys, pickSections(oldData, legacyData), filter(complement(isNil)));

const View = ({ data, localeMessage }) => {
  const latestStatus = take(
    1,
    data.filter(d => d.documentType === 'status'),
  )[0];
  const latestPREDSS = take(
    1,
    data.filter(d => d.documentType === 'predss'),
  )[0];

  const displayedPREDSS = latestPREDSS ? latestPREDSS : latestStatus;

  return (
    <Grid fluid style={{ height: '100%' }}>
      {displayedPREDSS && (!latestStatus || !isLegacyDocument(latestStatus)) && (
        <Row middle="xs" style={{ paddingBottom: '0.3rem' }}>
          <Col xs={4}>{localeMessage('status.prEDSS.titleShort')}</Col>
          <Col xs={8}>
            <EDSSIndicator
              severity={getPrEDSS(displayedPREDSS)}
              title={localeMessage('status.prEDSS.titleShort')}
              description={localeMessage(descr('prEDSS', getPrEDSS(displayedPREDSS)))}
            />
          </Col>
        </Row>
      )}
      <Row middle="xs">
        <Col xs>
          {!latestStatus || !latestStatus.sections
            ? null
            : map(r => (
                <Row key={r.join('_')} middle="xs">
                  {map(k => (
                    <Col xs={6} key={k}>
                      <Row middle="xs" style={{ paddingBottom: '0.3rem' }}>
                        <Col xs={8}>{localeMessage(section(k.split('VASValue')[0]))}</Col>
                        <Col xs={4}>
                          <Indicator
                            severity={accessData(latestStatus)[k] > 0 ? accessData(latestStatus)[k] : false}
                            title={k}
                            description={
                              k.indexOf('VASValue') < 0
                                ? accessData(latestStatus)[k]
                                  ? localeMessage(descr(k, accessData(latestStatus)[k]))
                                  : localeMessage('status.opts.unimpairing')
                                : undefined
                            }
                          />
                        </Col>
                      </Row>
                    </Col>
                  ))(r)}
                </Row>
              ))(filterKeys(isLegacyDocument(latestStatus), isLegacyStatus(latestStatus))(accessData(latestStatus)))}
        </Col>
      </Row>
      <div style={{ position: 'absolute', right: '15px', top: '13px' }}>
        {data[0] && data[0].createDate ? formatDate(data[0].createDate) : null}
      </div>
    </Grid>
  );
};

View.propTypes = {
  localeMessage: PropTypes.func.isRequired,
  data: PropTypes.array,
};

export default createSummaryTile(View, { module: ['status', 'predss'], localeId: 'status.title', routeTo: path, icon });
